import type { FC } from "react";

import { AIActionSuggestion } from "../../../ai/components/AIActionSuggestion";

import { displayOutcomeName } from "components/outcomes/helpers";
import { t } from "i18n/i18n";
import { OutcomeChip } from "mds/components/OutcomeChip";
import { ScoreIcon } from "mds/icons";
import {
  NestedOutcomeGroupType,
  NestedOutcomeSubgroupType,
  OutcomeWithScoreType,
} from "store/selectors";
import { homeOutcomesUrl } from "utils/urls";

export type OutcomeGroupBoxProps = {
  outcomeGroup: NestedOutcomeGroupType;
};

const OutcomeWithScore: FC<{ outcome: OutcomeWithScoreType }> = ({ outcome }) => {
  return (
    <OutcomeChip
      score={outcome.averageScore}
      title={displayOutcomeName(outcome)}
      to={homeOutcomesUrl("student", outcome.id)}
      floor
    />
  );
};

const SubgroupList: FC<{ subgroup: NestedOutcomeSubgroupType }> = ({ subgroup }) => {
  const outcomeList = subgroup.outcomes.map((outcome) => (
    <OutcomeWithScore key={outcome.id} outcome={outcome} />
  ));

  return (
    <div className="mb-2" key={subgroup.id}>
      <div className="mx-4 mb-2 flex items-center gap-1 text-sm font-medium">
        {subgroup.title} <ScoreIcon value={subgroup.averageOutcomeScore} floor />
      </div>

      <div className="flex w-full flex-wrap items-center gap-2 px-6">{outcomeList}</div>
    </div>
  );
};

export const OutcomeGroupBox: FC<OutcomeGroupBoxProps> = ({ outcomeGroup }) => {
  const subgroupList = outcomeGroup.outcomeSubgroups.map((subgroup) => (
    <SubgroupList key={subgroup.id} subgroup={subgroup} />
  ));

  const outcomeList = outcomeGroup.outcomes.map((outcome) => (
    <OutcomeWithScore key={outcome.id} outcome={outcome} />
  ));

  return (
    <div className="w-full rounded-xl border border-black-tint-90 p-6">
      <h3 className="mb-4 flex items-center gap-2 text-blue-shade-20">
        {outcomeGroup.title} <ScoreIcon value={outcomeGroup.averageOutcomeScore} floor />
      </h3>

      {subgroupList}
      <div className="flex w-full flex-wrap items-center gap-2 px-6">{outcomeList}</div>

      <div className="mt-6 text-[10px] text-black-tint-40">
        {t("progress_overview.actions.title")}
      </div>

      {/* TODO: This action requires careful consideration around performance. Not vital, so hiding the button for now. */}
      {/* <AIActionSuggestion
        fragment={t("progress_overview.actions.best_category.fragment")}
        fullText={t("progress_overview.actions.best_category.description", {
          groupName: outcomeGroup.title,
        })}
      /> */}

      <AIActionSuggestion
        fragment={t("progress_overview.actions.practice.fragment")}
        fullText={t("progress_overview.actions.practice.description", {
          groupName: outcomeGroup.title,
        })}
      />

      <AIActionSuggestion
        fragment={t("progress_overview.actions.after_school.fragment")}
        fullText={t("progress_overview.actions.after_school.description", {
          groupName: outcomeGroup.title,
        })}
      />
    </div>
  );
};
